import React from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Header, Footer, SEO } from '../../../shared/components';

const APP_NAME = import.meta.env.VITE_APP_NAME === 'plotmagic' ? 'PlotMagic' : 'RRD Planners';
const LEGAL_NAME = 'Kuthati Sai Ram';
const EMAIL = 'rrdplotmagic@gmail.com';
const PHONE = '+91 9573766436';

const RefundPage: React.FC = () => {
  return (
    <PageLayout size="full">
      <SEO title={`Refund Policy - ${APP_NAME}`} description="Our refund and return policy" />
      <Header />
      <PageLayout.Content>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-8">
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold text-foreground">Refund & Return Policy</h1>
              <p className="text-muted-foreground">Last updated: April 2026</p>
            </div>

            <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">1. Nature of Service</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {APP_NAME} is a digital service platform that provides plot map generation and PDF downloads using a credit-based system. Since our products are digital in nature (PDF documents generated and delivered electronically), they cannot be "returned" in the traditional sense.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">2. Refund Eligibility</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">Refunds may be issued in the following circumstances:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li><strong>Duplicate Payment:</strong> If you were charged twice for the same credit pack purchase due to a technical error</li>
                  <li><strong>Failed Delivery:</strong> If credits were not added to your account after a successful payment</li>
                  <li><strong>Technical Error:</strong> If a PDF download failed or was corrupted and credits were deducted, we will restore the credits to your account</li>
                  <li><strong>Unused Credits:</strong> If you request a refund within 7 days of purchasing a credit pack and have not used any of the purchased credits</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">3. Non-Refundable Cases</h2>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Credits that have already been consumed for PDF downloads</li>
                  <li>Signup bonus credits (these are complimentary and non-refundable)</li>
                  <li>Requests made after 7 days of purchase</li>
                  <li>Dissatisfaction with the plot map output (you may adjust and re-download at the edit rate)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">4. How to Request a Refund</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">To request a refund, contact us with:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Your registered email address</li>
                  <li>Transaction/payment ID</li>
                  <li>Reason for refund request</li>
                  <li>Date of purchase</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Email: <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a><br />
                  Phone: <a href={`tel:${PHONE}`} className="text-primary hover:underline">{PHONE}</a>
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">5. Refund Process</h2>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Refund requests are reviewed within 2 business days</li>
                  <li>Approved refunds are processed within 5-7 working days</li>
                  <li>Refunds are credited back to the original payment method (bank account, UPI, or card used for payment)</li>
                  <li>You will receive an email confirmation once the refund is processed</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">6. Credit Restoration</h2>
                <p className="text-muted-foreground leading-relaxed">
                  In cases of technical failures where credits were deducted but the download was unsuccessful, credits will be restored to your account within 24 hours of reporting the issue. No separate refund request is needed for credit restoration.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">7. Contact</h2>
                <p className="text-muted-foreground leading-relaxed">
                  <strong>{LEGAL_NAME}</strong><br />
                  Email: <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a><br />
                  Phone: <a href={`tel:${PHONE}`} className="text-primary hover:underline">{PHONE}</a>
                </p>
              </section>
            </div>

            <div className="text-center pt-8 border-t border-border">
              <a href="/" className="inline-flex items-center text-primary hover:underline font-medium">← Back to Home</a>
            </div>
          </div>
        </div>
      </PageLayout.Content>
      <Footer />
    </PageLayout>
  );
};

export default RefundPage;
